import styles from './OrganoidInfoTab.module.css';
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DraftModal from '../../components/modal/DraftModal';
import MultiLargeFileUploads from '../../components/modal/MultiLargeFileUploads';
import NGSListmodal from '../../components/modal/NGSListModal';
// import LargeFileUploads from '../../components/modal/LargeFileUploads'; // 필요 시 사용

function OrganoidInfoTab() {
    const { t } = useTranslation();
    const { tmpKey } = useParams();

    const [isUploadModal, setIsUploadModal] = useState(false);
    const [organoidInfo, setOrganoidInfo] = useState({});
    const [isEditmode, setIsEditMode] = useState(false);
    const [isDraftModalOpen, setIsDraftModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('save'); // 'save' | 'delete'
    const [draftCtx, setDraftCtx] = useState(null);     // { mode, opinion }

    // ★ [추가] 데이터가 아예 없는 '신규 등록' 상태인지 판별하는 변수
    const isNewData = !organoidInfo || !organoidInfo[0] || Object.keys(organoidInfo[0]).length === 0;

    // ★ [추가] NGS 모달 상태 관리 (isOpen, type: 'WES' | 'RNA')
    const [ngsModalState, setNgsModalState] = useState({ isOpen: false, type: '' });

    // 보기용: 'hashmark' → '#'
    const hashmarkToSharp = (word) => {
        if (!word) return '';
        return word.replaceAll('hashmark', '#');
    };
    // 저장용: '#' → 'hashmark'
    const sharpToHashmark = (word) => {
        if (!word) return '';
        return word.replaceAll('#', 'hashmark');
    };

    // ★ [추가] NGS 모달 열기/닫기 핸들러
    const openNGSModal = (type) => {
        setNgsModalState({ isOpen: true, type });
    };
    const closeNGSModal = () => {
        setNgsModalState({ isOpen: false, type: '' });
    };

    // ★ [추가] ESC 키로 모달 닫기
    useEffect(() => {
        const handleEsc = (event) => {
            if (event.key === 'Escape') {
                closeNGSModal();
            }
        };
        window.addEventListener('keydown', handleEsc);
        return () => {
            window.removeEventListener('keydown', handleEsc);
        };
    }, []);

    const clickUploadModalOpen = () => setIsUploadModal(!isUploadModal);
    const clickUploadModalClose = () => setIsUploadModal(false);

    const [formData, setFormData] = useState({
        Key: '',
        SampleType: '',
        Organism: '',
        Type: '',
        Subtype: '',
        Orgin: '',
        Pair: '',
        immuneCellPair: '', // ★ [추가] Immune cell pair
        CAFPair: '',        // ★ [추가] CAF pair
        establishmentNo: '',
        MutationInformation: '',
        splitRatio: '',
        daysBetweenPassaging: '',
        surgeryDate: '',
        WES: '',
        RNASeq: '',
        MSS: '',
        sampleSource: '',
        site: '',
        cro_status: '',     // ★ [추가] CRO stat
    });

    // ★ 새로 제안하는 GBCC Code(입력칸)
    const [newKeyInput, setNewKeyInput] = useState('');

    // ★ [수정] 데이터 유무 확인 후 편집 모드 진입
    const handleEditClick = () => {
        if (!isEditmode && isNewData) {
            alert(t('등록된 오가노이드 정보가 없습니다. 새로운 정보를 추가합니다.'));
            // 신규 데이터일 경우, New GBCC Code 입력칸에 현재 URL의 키값을 기본으로 세팅 (화면 표시용으로 '#' 변환)
            setNewKeyInput(hashmarkToSharp(tmpKey));
        }
        setIsEditMode(!isEditmode);
    };

    const handleSaveClick = () => {
        setModalMode('save');
        setIsDraftModalOpen(true);
    };

    // 삭제 시에는 기존처럼 데이터가 없으면 차단
    const handleDeleteClick = () => {
        if (isNewData) {
            alert(t('등록된 오가노이드 정보가 존재하지 않아 삭제할 수 없습니다.'));
            return;
        }
        setModalMode('delete');
        setIsDraftModalOpen(true);
    };

    const closeDraftModal = () => setIsDraftModalOpen(false);

    const onDraftSubmit = (opinion) => {
        setDraftCtx({ mode: modalMode, opinion });
        setIsDraftModalOpen(false);
    };

    // 기안 제출 처리 (save / delete 분기)
    useEffect(() => {
        if (!draftCtx) return;

        const { mode, opinion } = draftCtx;
        const prevKey = formData.Key || tmpKey || ''; // DB 현재 Key

        // 공통 페이로드(현재 상태 스냅샷 + 기안의견)
        const basePayload = {
            ...formData,
            Key: prevKey,         // 실제 Key는 바꾸지 않음
            prevKey,              // 기준 Key
            draftOpinion: opinion
        };

        let payload;
        if (mode === 'save') {
            const draftNewKey = newKeyInput.trim()
                ? sharpToHashmark(newKeyInput.trim())
                : null;
            payload = {
                ...basePayload,
                draftNewKey,
                action: 'key-change-draft'
            };
        } else { // delete
            payload = {
                ...basePayload,
                action: 'delete-draft'
            };
        }

        fetch('/api/updateOraganoidInfo', {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify(payload),
        })
            .then(r => r.json())
            .then(data => {
                alert(data.message);
                setIsEditMode(false);
                if (mode === 'save' && !isNewData) setNewKeyInput(''); // 기존 데이터 수정 성공 시에만 입력칸 비움
            })
            .finally(() => {
                setDraftCtx(null);  // 재트리거 방지
            });
    }, [draftCtx]); // eslint-disable-line react-hooks/exhaustive-deps

    // 기본 정보 조회
    useEffect(() => {
        const reqOption = {
            method: 'post',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ param: tmpKey }),
        };
        fetch('/api/getOrganoidInfo', reqOption)
            .then((res) => res.json())
            .then((data) => setOrganoidInfo(data));
    }, [tmpKey]);

    // 조회값 → 폼 상태 동기화
    useEffect(() => {
        if (!organoidInfo[0]) return;
        const o = organoidInfo[0];
        setFormData({
            Key: o.Key || '',
            SampleType: o.SampleType || '',
            Organism: o.Organism || '',
            Type: o.Type || '',
            Subtype: o.Subtype || '',
            Orgin: o.Orgin || '',
            Pair: o.Pair || '',
            immuneCellPair: o.immuneCellPair || '', // ★ [추가] 동기화
            CAFPair: o.CAFPair || '',               // ★ [추가] 동기화
            establishmentNo: o.establishmentNo || '',
            MutationInformation: o.MutationInformation || '',
            splitRatio: o.splitRatio || '',
            daysBetweenPassaging: o.daysBetweenPassaging || '',
            surgeryDate: o.surgeryDate || '',
            WES: o.WES || '',
            RNASeq: o.RNASeq || '',
            MSS: o.MSS || '',
            sampleSource: o.sampleSource || '',
            site: o.site || '',
            cro_status: o.cro_status || '',         // ★ [추가] 동기화
        });

        // 기존 데이터가 있으면 새 입력칸 비우기
        if (Object.keys(o).length > 0) {
            setNewKeyInput('');
        }
    }, [organoidInfo]);

    return (
        <div className={styles.outerBox}>
            {isDraftModalOpen && (
                <DraftModal
                    mode={modalMode}
                    onClose={closeDraftModal}
                    onSubmit={onDraftSubmit}
                />
            )}

            {ngsModalState.isOpen && (
                <NGSListmodal
                    isOpen={ngsModalState.isOpen}
                    onClose={closeNGSModal}
                    type={ngsModalState.type}
                    organoidKey={tmpKey}
                />
            )}

            <div className={styles.basicInformation}>
                <span className={styles.title}>Basic Information</span>
                <span className={styles.editButton} onClick={handleEditClick}>edit</span>
                {isEditmode && (
                    <>
                        <span className={styles.editButton} onClick={handleSaveClick}>save</span>
                        <span className={styles.editButton} onClick={handleDeleteClick}>delete</span>
                    </>
                )}
            </div>

            <div className={styles.contents}>
                {/* GBCC Code (현재값: 읽기 전용 표시) */}
                <div className={styles.leftContent}>GBCC Code</div>
                <div className={styles.rightContent}>{hashmarkToSharp(formData.Key || tmpKey)}</div>

                {/* 편집 모드일 때만 새 GBCC Code 입력칸 노출 */}
                {isEditmode && (
                    <>
                        <div className={styles.leftContent}>New GBCC Code</div>
                        {/* ★ [수정] 불필요한 div 랩핑을 없애고, 다른 input들과 동일하게 클래스 바로 적용 */}
                        <input
                            className={styles.rightContent}
                            style={{
                                background: isNewData ? '#f0f0f0' : 'white',
                                cursor: isNewData ? 'not-allowed' : 'text'
                            }}
                            onClick={() => {
                                if (isNewData) {
                                    alert(t('신규 등록 이후 변경 가능합니다.'));
                                }
                            }}
                            value={newKeyInput}
                            onChange={(e) => {
                                if (!isNewData) setNewKeyInput(e.target.value);
                            }}
                            placeholder={t("예: GBC067001T#1 (미입력 시 유지)")}
                            readOnly={isNewData} // 신규 데이터면 읽기 전용
                        />
                    </>
                )}

                <div className={styles.leftContent}>Disease Type</div>
                {isEditmode ? (
                    <input
                        value={formData.Type}
                        onChange={(e) => setFormData({ ...formData, Type: e.target.value })}
                        className={styles.rightContent}
                    />
                ) : (
                    <div className={styles.rightContent}>{organoidInfo[0]?.Type}</div>
                )}

                <div className={styles.leftContent}>Subtype</div>
                {isEditmode ? (
                    <input
                        value={formData.Subtype}
                        onChange={(e) => setFormData({ ...formData, Subtype: e.target.value })}
                        className={styles.rightContent}
                    />
                ) : (
                    <div className={styles.rightContent}>{organoidInfo[0]?.Subtype}</div>
                )}

                <div className={styles.leftContent}>Sample Source</div>
                {isEditmode ? (
                    <select
                        value={formData.sampleSource ?? organoidInfo[0]?.sampleSource ?? ''}
                        onChange={(e) => setFormData({ ...formData, sampleSource: e.target.value })}
                        className={styles.rightContent}
                    >
                        <option value="">-</option>
                        <option value="Surgical tissue">Surgical tissue</option>
                        <option value="Biopsy tissue">Biopsy tissue</option>
                        <option value="Ascites">Ascites</option>
                        <option value="Pleural effusion">Pleural effusion</option>
                        <option value="Pericardial effusion">Pericardial effusion</option>
                        <option value="PDX">PDX</option>
                    </select>
                ) : (
                    <div className={styles.rightContent}>{organoidInfo[0]?.sampleSource || '-'}</div>
                )}

                <div className={styles.leftContent}>Sample Type</div>
                {isEditmode ? (
                    <select
                        value={formData.SampleType ?? organoidInfo[0]?.SampleType ?? ''}
                        onChange={(e) => setFormData({ ...formData, SampleType: e.target.value })}
                        className={styles.rightContent}
                    >
                        <option value="">-</option>
                        <option value="Metastasis">Metastasis</option>
                        <option value="Primary">Primary</option>
                    </select>
                ) : (
                    <div className={styles.rightContent}>{organoidInfo[0]?.SampleType}</div>
                )}

                <div className={styles.leftContent}>Site</div>
                {isEditmode ? (
                    <input
                        value={formData.site}
                        onChange={(e) => setFormData({ ...formData, site: e.target.value })}
                        className={styles.rightContent}
                    />
                ) : (
                    <div className={styles.rightContent}>{organoidInfo[0]?.site}</div>
                )}

                {/* ★ [추가] CRO Stat 시작 */}
                <div className={styles.leftContent}>CRO Stat</div>
                {isEditmode ? (
                    <input
                        value={formData.cro_status}
                        onChange={(e) => setFormData({ ...formData, cro_status: e.target.value })}
                        className={styles.rightContent}
                    />
                ) : (
                    <div className={styles.rightContent}>{organoidInfo[0]?.cro_status}</div>
                )}
                {/* ★ [추가] CRO Stat 끝 */}

                <div className={styles.leftContent}>Surgery Date</div>
                {isEditmode ? (
                    <input
                        value={formData.surgeryDate}
                        onChange={(e) => setFormData({ ...formData, surgeryDate: e.target.value })}
                        className={styles.rightContent}
                    />
                ) : (
                    <div className={styles.rightContent}>{organoidInfo[0]?.surgeryDate}</div>
                )}

                {/* <div className={styles.leftContent}>Establishment no.</div>
                {isEditmode ? (
                    <input
                        value={formData.establishmentNo}
                        onChange={(e) => setFormData({ ...formData, establishmentNo: e.target.value })}
                        className={styles.rightContent}
                    />
                ) : (
                    <div className={styles.rightContent}>{organoidInfo[0]?.establishmentNo}</div>
                )} */}

                <div className={styles.leftContent}>N/T Pair</div>
                {isEditmode ? (
                    <select
                        value={formData.Pair ?? organoidInfo[0]?.Pair ?? ''}
                        onChange={(e) => setFormData({ ...formData, Pair: e.target.value })}
                        className={styles.rightContent}
                    >
                        <option value="">-</option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                    </select>
                ) : (
                    <div className={styles.rightContent}>{organoidInfo[0]?.Pair}</div>
                )}

                {/* ★ [추가] Immune cell pair 시작 */}
                <div className={styles.leftContent}>Immune Cell Pair</div>
                {isEditmode ? (
                    <select
                        value={formData.immuneCellPair ?? organoidInfo[0]?.immuneCellPair ?? ''}
                        onChange={(e) => setFormData({ ...formData, immuneCellPair: e.target.value })}
                        className={styles.rightContent}
                    >
                        <option value="">-</option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                    </select>
                ) : (
                    <div className={styles.rightContent}>{organoidInfo[0]?.immuneCellPair}</div>
                )}
                {/* ★ [추가] Immune cell pair 끝 */}

                {/* ★ [추가] CAF pair 시작 */}
                <div className={styles.leftContent}>CAF Pair</div>
                {isEditmode ? (
                    <select
                        value={formData.CAFPair ?? organoidInfo[0]?.CAFPair ?? ''}
                        onChange={(e) => setFormData({ ...formData, CAFPair: e.target.value })}
                        className={styles.rightContent}
                    >
                        <option value="">-</option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                    </select>
                ) : (
                    <div className={styles.rightContent}>{organoidInfo[0]?.CAFPair}</div>
                )}
                {/* ★ [추가] CAF pair 끝 */}

                <div className={styles.leftContent}>WES</div>
                {isEditmode ? (
                    <select
                        value={formData.WES ?? organoidInfo[0]?.WES ?? ''}
                        onChange={(e) => setFormData({ ...formData, WES: e.target.value })}
                        className={styles.rightContent}
                    >
                        <option value="">-</option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                    </select>
                ) : (
                    <div
                        className={styles.rightContent}
                        style={{ cursor: 'pointer', textDecoration: 'underline', color: 'blue' }}
                        onClick={() => openNGSModal('WES')}
                    >
                        {organoidInfo[0]?.WES}
                    </div>
                )}

                <div className={styles.leftContent}>RNA-Seq</div>
                {isEditmode ? (
                    <select
                        value={formData.RNASeq ?? organoidInfo[0]?.RNASeq ?? ''}
                        onChange={(e) => setFormData({ ...formData, RNASeq: e.target.value })}
                        className={styles.rightContent}
                    >
                        <option value="">-</option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                    </select>
                ) : (
                    <div
                        className={styles.rightContent}
                        style={{ cursor: 'pointer', textDecoration: 'underline', color: 'blue' }}
                        onClick={() => openNGSModal('RNA')}
                    >
                        {organoidInfo[0]?.RNASeq}
                    </div>
                )}

                <div className={styles.leftContent}>Mutation Information</div>
                {isEditmode ? (
                    <textarea
                        value={formData.MutationInformation}
                        onChange={(e) => setFormData({ ...formData, MutationInformation: e.target.value })}
                        className={styles.rightContent}
                    />
                ) : (
                    <div className={styles.rightContent}>
                        {organoidInfo[0]?.MutationInformation?.split('\n').map((line, index) => (
                            <React.Fragment key={index}>
                                {line}
                                <br />
                            </React.Fragment>
                        ))}
                    </div>
                )}

                <div className={styles.leftContent}>Split Ratio</div>
                {isEditmode ? (
                    <input
                        value={formData.splitRatio}
                        onChange={(e) => setFormData({ ...formData, splitRatio: e.target.value })}
                        className={styles.rightContent}
                    />
                ) : (
                    <div className={styles.rightContent}>{organoidInfo[0]?.splitRatio}</div>
                )}

                <div className={styles.leftContent}>Days Between Passaging</div>
                {isEditmode ? (
                    <input
                        value={formData.daysBetweenPassaging}
                        onChange={(e) => setFormData({ ...formData, daysBetweenPassaging: e.target.value })}
                        className={styles.rightContent}
                    />
                ) : (
                    <div className={styles.rightContent}>{organoidInfo[0]?.daysBetweenPassaging}</div>
                )}

                <div className={styles.leftContent}>PDO Annotation</div>
                {isEditmode ? (
                    <textarea
                        value={formData.MSS}
                        onChange={(e) => setFormData({ ...formData, MSS: e.target.value })}
                        className={styles.rightContent}
                    />
                ) : (
                    <div className={styles.rightContent}>
                        {organoidInfo[0]?.MSS?.split('\n').map((line, index) => (
                            <React.Fragment key={index}>
                                {line}
                                <br />
                            </React.Fragment>
                        ))}
                    </div>
                )}
            </div>

            {(organoidInfo[0]?.RNASeq || '').toLowerCase() === 'no' && (
                <div onClick={clickUploadModalOpen} className={styles.uploadButton}>
                    Raw data upload
                </div>
            )}

            {isUploadModal && <MultiLargeFileUploads onClose={clickUploadModalClose} />}
        </div>
    );
}

export default OrganoidInfoTab;